import React, { useEffect, useMemo, useState } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Chip,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import { useAuth } from '../../contexts/authContext';
import { useAlert } from '../../contexts/alertContext';
import { useTitle } from '../../hooks/titleProvider';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { getOngoingEvents } from '../../features/event/eventSlice';

const WorkspaceSettings = () => {
  const { setTitle } = useTitle();
  const {
    currentWorkspace,
    currentUser,
    currentUserProfile,
    workspaces,
    workspaceRequired,
    createInitialWorkspace,
    createWorkspace,
    switchWorkspace,
    renameCurrentWorkspace,
    addWorkspaceMember,
    updateWorkspaceMemberRole,
    removeWorkspaceMember
  } = useAuth();
  const { showAlertSuccess, showAlertError } = useAlert();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { workspaceId } = useParams();

  const defaultWorkspaceName = currentUserProfile?.firstName
    ? `${currentUserProfile.firstName}'s Workspace`
    : 'My Workspace';

  const [createWorkspaceName, setCreateWorkspaceName] = useState(defaultWorkspaceName);
  const [renameWorkspaceName, setRenameWorkspaceName] = useState('');
  const [memberName, setMemberName] = useState('');
  const [memberEmail, setMemberEmail] = useState('');
  const [memberRole, setMemberRole] = useState('User');
  const [savingName, setSavingName] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [updatingRoleKey, setUpdatingRoleKey] = useState('');
  const [removingMemberKey, setRemovingMemberKey] = useState('');
  const [creatingWorkspace, setCreatingWorkspace] = useState(false);
  const [memberListTab, setMemberListTab] = useState(0);

  const sortedWorkspaces = useMemo(() => {
    return [...(workspaces || [])].sort((a, b) =>
      String(a?.name || '').localeCompare(String(b?.name || ''))
    );
  }, [workspaces]);

  const selectedWorkspace = workspaceId
    ? sortedWorkspaces.find((workspace) => workspace.id === workspaceId) || null
    : null;

  const activeWorkspace = selectedWorkspace || currentWorkspace;

  const members = Array.isArray(activeWorkspace?.members) ? activeWorkspace.members : [];
  const pendingMembers = Array.isArray(activeWorkspace?.pendingMembers)
    ? activeWorkspace.pendingMembers
    : [];

  const sortedMembers = [...members].sort((a, b) => {
    const aName = String(a?.name || a?.email || '').toLowerCase();
    const bName = String(b?.name || b?.email || '').toLowerCase();
    return aName.localeCompare(bName);
  });

  const sortedPendingMembers = [...pendingMembers].sort((a, b) => {
    const aName = String(a?.name || a?.email || '').toLowerCase();
    const bName = String(b?.name || b?.email || '').toLowerCase();
    return aName.localeCompare(bName);
  });

  const ownerMember = members.find((member) => member?.id === activeWorkspace?.ownerId);
  const ownerDisplayName = String(
    ownerMember?.name || ownerMember?.email || activeWorkspace?.ownerId || '-'
  ).trim();

  useEffect(() => {
    setTitle(workspaceId ? 'Workspace Settings' : 'Workspaces');
  }, [setTitle, workspaceId]);

  useEffect(() => {
    if (!workspaceId) {
      return;
    }

    if (currentWorkspace?.id !== workspaceId) {
      switchWorkspace(workspaceId).catch(() => {
        showAlertError('Unable to load workspace settings.');
      });
    }
  }, [workspaceId, currentWorkspace?.id, switchWorkspace, showAlertError]);

  useEffect(() => {
    setRenameWorkspaceName(activeWorkspace?.name || '');
  }, [activeWorkspace?.id, activeWorkspace?.name]);

  const isOwner = activeWorkspace?.ownerId && currentUser?.uid === activeWorkspace.ownerId;

  const handleRename = async () => {
    if (!isOwner) {
      showAlertError('Only the workspace owner can rename this workspace.');
      return;
    }

    const trimmed = String(renameWorkspaceName || '').trim();
    if (!trimmed) {
      showAlertError('Workspace name is required.');
      return;
    }

    try {
      setSavingName(true);
      await renameCurrentWorkspace(trimmed);
      showAlertSuccess('Workspace renamed successfully.');
    } catch (error) {
      showAlertError(error?.response?.data || error?.message || 'Failed to rename workspace.');
    } finally {
      setSavingName(false);
    }
  };

  const handleAddMember = async () => {
    if (!isOwner) {
      showAlertError('Only the workspace owner can add members.');
      return;
    }

    const trimmedEmail = String(memberEmail || '').trim();
    const trimmedName = String(memberName || '').trim();

    if (!trimmedEmail) {
      showAlertError('Enter an email address.');
      return;
    }

    if (!trimmedName) {
      showAlertError('Enter a name for the invite record.');
      return;
    }

    try {
      setAddingMember(true);
      await addWorkspaceMember(trimmedEmail, memberRole, trimmedName, trimmedEmail);
      setMemberName('');
      setMemberEmail('');
      setMemberRole('User');
      showAlertSuccess('Invite sent successfully.');
    } catch (error) {
      showAlertError(error?.response?.data || error?.message || 'Failed to add member.');
    } finally {
      setAddingMember(false);
    }
  };

  const handleCreateInitialWorkspace = async () => {
    const trimmed = String(createWorkspaceName || '').trim();
    if (!trimmed) {
      showAlertError('Workspace name is required.');
      return;
    }

    try {
      setCreatingWorkspace(true);
      await createInitialWorkspace(trimmed);
      showAlertSuccess('Workspace created successfully.');
      navigate('/overview');
    } catch (error) {
      showAlertError(error?.response?.data || error?.message || 'Failed to create workspace.');
    } finally {
      setCreatingWorkspace(false);
    }
  };

  const handleCreateWorkspace = async () => {
    const trimmed = String(createWorkspaceName || '').trim();
    if (!trimmed) {
      showAlertError('Workspace name is required.');
      return;
    }

    try {
      setCreatingWorkspace(true);
      await createWorkspace(trimmed);
      await dispatch(getOngoingEvents());
      showAlertSuccess('Workspace created successfully.');
      navigate('/overview');
    } catch (error) {
      showAlertError(error?.response?.data || error?.message || 'Failed to create workspace.');
    } finally {
      setCreatingWorkspace(false);
    }
  };

  const handleRemoveMember = async ({ memberId, email, inviteId, pending, displayName }) => {
    if (!isOwner) {
      showAlertError('Only the workspace owner can manage members.');
      return;
    }

    const actionLabel = pending ? 'delete invite for' : 'remove';
    const target = displayName || email || 'this member';
    const confirmed = window.confirm(`Are you sure you want to ${actionLabel} ${target}?`);
    if (!confirmed) {
      return;
    }

    const key = `${pending ? 'pending' : 'member'}-${inviteId || memberId || email || target}`;

    try {
      setRemovingMemberKey(key);
      await removeWorkspaceMember(memberId, email, inviteId, pending);
      showAlertSuccess(pending ? 'Invite deleted.' : 'Member removed from workspace.');
    } catch (error) {
      showAlertError(
        error?.response?.data || error?.message || 'Failed to update workspace member.'
      );
    } finally {
      setRemovingMemberKey('');
    }
  };

  const handleRoleChange = async (memberId, nextRole) => {
    if (!isOwner || !memberId) {
      return;
    }

    try {
      setUpdatingRoleKey(memberId);
      await updateWorkspaceMemberRole(memberId, nextRole);
      showAlertSuccess('Member role updated.');
    } catch (error) {
      showAlertError(error?.response?.data || error?.message || 'Failed to update member role.');
    } finally {
      setUpdatingRoleKey('');
    }
  };

  if (workspaceRequired || (!currentWorkspace && sortedWorkspaces.length === 0)) {
    return (
      <Stack spacing={2}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1.5}>
              <Typography variant="h6">Create Your Workspace</Typography>
              <Typography variant="body2" color="text.secondary">
                You need a workspace before using the app.
              </Typography>
              <TextField
                value={createWorkspaceName}
                onChange={(e) => setCreateWorkspaceName(e.target.value)}
                label="Workspace name"
                size="small"
              />
              <Box>
                <Button
                  variant="contained"
                  onClick={handleCreateInitialWorkspace}
                  disabled={creatingWorkspace}>
                  Create Workspace
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    );
  }

  if (!workspaceId) {
    return (
      <Stack spacing={2}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1.5}>
              <Typography variant="h6">Your Workspaces</Typography>
              {sortedWorkspaces.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No workspaces found.
                </Typography>
              ) : (
                <List disablePadding>
                  {sortedWorkspaces.map((workspace, index) => (
                    <React.Fragment key={workspace.id}>
                      <ListItem
                        disableGutters
                        secondaryAction={
                          <Button
                            size="small"
                            onClick={() => navigate(`/workspace-settings/${workspace.id}`)}>
                            Manage
                          </Button>
                        }>
                        <ListItemText
                          primary={workspace.name || 'Workspace'}
                          secondary={
                            workspace.id === currentWorkspace?.id ? 'Active workspace' : ''
                          }
                        />
                      </ListItem>
                      {index < sortedWorkspaces.length - 1 ? <Divider component="li" /> : null}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1.5}>
              <Typography variant="h6">Create Workspace</Typography>
              <Typography variant="body2" color="text.secondary">
                Create your own workspace. You will automatically switch to it.
              </Typography>
              <TextField
                value={createWorkspaceName}
                onChange={(e) => setCreateWorkspaceName(e.target.value)}
                label="Workspace name"
                size="small"
              />
              <Box>
                <Button
                  variant="contained"
                  onClick={handleCreateWorkspace}
                  disabled={creatingWorkspace}>
                  Create Workspace
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    );
  }

  if (!activeWorkspace || currentWorkspace?.id !== workspaceId) {
    return (
      <Stack spacing={2}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              Loading workspace settings...
            </Typography>
          </CardContent>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Box>
        <Button size="small" onClick={() => navigate('/workspace-settings')}>
          Back to Workspaces
        </Button>
      </Box>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant="h6">Current Workspace</Typography>
            <Typography variant="body2" color="text.secondary">
              {activeWorkspace?.name || 'No workspace selected'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Owner: {ownerDisplayName || '-'}
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant="h6">Rename Workspace</Typography>
            <TextField
              value={renameWorkspaceName}
              onChange={(e) => setRenameWorkspaceName(e.target.value)}
              label="Workspace name"
              size="small"
              disabled={!isOwner}
            />
            <Box>
              <Button variant="contained" onClick={handleRename} disabled={savingName || !isOwner}>
                Save Name
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant="h6">Add Workspace Member</Typography>
            <Typography variant="body2" color="text.secondary">
              Enter a name and email to invite. If the user already exists, their platform profile
              is used.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                fullWidth
                value={memberName}
                onChange={(e) => setMemberName(e.target.value)}
                label="Invite Name"
                size="small"
                disabled={!isOwner}
              />
              <TextField
                fullWidth
                value={memberEmail}
                onChange={(e) => setMemberEmail(e.target.value)}
                label="Invite Email"
                size="small"
                disabled={!isOwner}
              />
              <TextField
                select
                value={memberRole}
                onChange={(e) => setMemberRole(e.target.value)}
                label="Member Role"
                size="small"
                disabled={!isOwner}
                sx={{ minWidth: { sm: 140 } }}>
                <MenuItem value="User">User</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
              </TextField>
            </Stack>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  variant="contained"
                  onClick={handleAddMember}
                  disabled={addingMember || !isOwner}>
                  Add Member
                </Button>
                {addingMember ? <CircularProgress size={18} /> : null}
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant="h6">Workspace Members</Typography>

            <Tabs
              value={memberListTab}
              onChange={(_, nextValue) => setMemberListTab(nextValue)}
              aria-label="Workspace members and invites tabs"
              sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tab label={`Members (${sortedMembers.length})`} />
              <Tab label={`Invites (${sortedPendingMembers.length})`} />
            </Tabs>

            {sortedMembers.length === 0 && sortedPendingMembers.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No members found in this workspace yet.
              </Typography>
            ) : (
              <List disablePadding>
                {memberListTab === 0
                  ? sortedMembers.map((member, index) => {
                      const memberName = String(member?.name || member?.email || 'User');
                      const memberEmail = String(member?.email || '').trim();
                      const memberRole = String(member?.role || 'User');
                      const isOwnerMember = member?.id === activeWorkspace?.ownerId;

                      return (
                        <React.Fragment key={`member-${memberEmail || memberName}-${index}`}>
                          <ListItem disableGutters sx={{ alignItems: 'center' }}>
                            <ListItemAvatar>
                              <Avatar>{memberName.charAt(0).toUpperCase()}</Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={memberName}
                              secondary={memberEmail || undefined}
                            />
                            <Stack direction="row" spacing={1} alignItems="center">
                              {isOwnerMember ? (
                                <Chip size="small" label={memberRole} />
                              ) : (
                                <TextField
                                  select
                                  size="small"
                                  value={memberRole}
                                  disabled={!isOwner || updatingRoleKey === member?.id}
                                  onChange={(event) =>
                                    handleRoleChange(member?.id, event.target.value)
                                  }
                                  sx={{ minWidth: 110 }}>
                                  <MenuItem value="User">User</MenuItem>
                                  <MenuItem value="Admin">Admin</MenuItem>
                                </TextField>
                              )}
                              {isOwnerMember ? (
                                <Chip size="small" color="primary" label="Owner" />
                              ) : null}
                              {!isOwnerMember ? (
                                <IconButton
                                  size="small"
                                  color="error"
                                  aria-label={`Remove ${memberName}`}
                                  disabled={
                                    removingMemberKey ===
                                    `member-${member?.id || memberEmail || memberName}`
                                  }
                                  onClick={() =>
                                    handleRemoveMember({
                                      memberId: member?.id,
                                      email: memberEmail,
                                      inviteId: null,
                                      pending: false,
                                      displayName: memberName
                                    })
                                  }>
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              ) : null}
                            </Stack>
                          </ListItem>
                          {index < sortedMembers.length - 1 ? <Divider component="li" /> : null}
                        </React.Fragment>
                      );
                    })
                  : null}

                {memberListTab === 1
                  ? sortedPendingMembers.map((member, index) => {
                      const memberName = String(member?.name || member?.email || 'Invited User');
                      const memberEmail = String(member?.email || '').trim();
                      const memberRole = String(member?.role || 'User');

                      return (
                        <React.Fragment key={`pending-${memberEmail || memberName}-${index}`}>
                          <ListItem disableGutters sx={{ alignItems: 'center' }}>
                            <ListItemAvatar>
                              <Avatar>{memberName.charAt(0).toUpperCase()}</Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={memberName}
                              secondary={memberEmail || 'Invitation pending'}
                            />
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip size="small" label={memberRole} />
                              <Chip size="small" color="warning" label="Invited" />
                              <IconButton
                                size="small"
                                color="error"
                                aria-label={`Delete invite for ${memberName}`}
                                disabled={
                                  removingMemberKey ===
                                  `pending-${member?.inviteId || member?.id || memberEmail || memberName}`
                                }
                                onClick={() =>
                                  handleRemoveMember({
                                    memberId: member?.id,
                                    email: memberEmail,
                                    inviteId: member?.inviteId,
                                    pending: true,
                                    displayName: memberName
                                  })
                                }>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </ListItem>
                          {index < sortedPendingMembers.length - 1 ? (
                            <Divider component="li" />
                          ) : null}
                        </React.Fragment>
                      );
                    })
                  : null}
              </List>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default WorkspaceSettings;
